import LazyYouTubeEmbed from '@/commons/LazyYouTubeEmbed';
import React from 'react';
import {
  FaTools,
  FaCogs,
  FaPumpSoap,
  FaShower,
  FaOilCan,
  FaRecycle,
  FaCheckCircle,
  FaCompressAlt,
  FaThermometerHalf,
} from 'react-icons/fa';
import { localiseTerms } from '../../../../utils/markets';

const chemicalOverhaulSteps = [
  {
    icon: <FaTools className="text-3xl text-orange-600" />,
    title: '1. Inspection & Preparation',
    description:
      'Technician inspects the unit and covers surrounding furniture and flooring to prevent mess from the cleaning process.',
  },
  {
    icon: <FaCogs className="text-3xl text-orange-600" />,
    title: '2. Dismantle The Aircon Unit',
    description:
      'The entire aircon unit is taken off the wall and disassembled into separate components for deep cleaning.',
  },
  {
    icon: <FaPumpSoap className="text-3xl text-orange-600" />,
    title: '3. Chemical Application',
    description:
      'A strong cleaning solution is applied to all key components including evaporator coils and fan blades to break down heavy buildup.',
  },
  {
    icon: <FaShower className="text-3xl text-orange-600" />,
    title: '4. Washing The Components',
    description:
      'Each disassembled part is washed thoroughly using running water or a jet spray to remove loosened contaminants.',
  },
  {
    icon: <FaOilCan className="text-3xl text-orange-600" />,
    title: '5. Lubrication of Fan Bearings',
    description:
      'Fan bearings are lubricated if required, to ensure smooth and quiet operation after the overhaul.',
  },
  {
    icon: <FaRecycle className="text-3xl text-orange-600" />,
    title: '6. Vacuuming of Drainage Pipe',
    description:
      'Drainage pipe is vacuumed to eliminate any clogs and ensure water flows out properly during operation.',
  },
  {
    icon: <FaCheckCircle className="text-3xl text-blue-500" />,
    title: '7. Reassembly & Testing',
    description:
      'Technician reassembles the cleaned aircon unit, reinstalls filters, and performs a full operational test.',
  },
  {
    icon: <FaCompressAlt className="text-3xl text-blue-500" />,
    title: '8. Compressor Performance Checking',
    description:
      'The outdoor compressor unit is checked for performance. Extra cleaning or servicing may be advised at an additional cost.',
  },
  {
    icon: <FaThermometerHalf className="text-3xl text-blue-500" />,
    title: '9. Monitor Refrigerant Gas Level',
    description:
      'Refrigerant gas levels are checked using a gauge. Technician will inform you if a refill is needed (chargeable).',
  },
];

// Optional props (all absent on the Singapore pages, which render exactly as
// before): `market` localises the terms and hides the Singapore-filmed video;
// `steps`, `title` and `intro` replace the copy, so the UAE / Malaysia pages
// can show their own wording (page sections must not repeat the SG text).
export const AirconChemicalOverhaulProcess = ({ market, steps: customSteps, title, intro } = {}) => {
  // Custom steps are plain titles; the SG titles carry their own numbers.
  const items = customSteps
    ? customSteps.map((step, i) => ({ ...step, icon: step.icon ?? chemicalOverhaulSteps[i % chemicalOverhaulSteps.length].icon, title: `${i + 1}. ${step.title}` }))
    : chemicalOverhaulSteps.map((step) => ({ ...step, title: localiseTerms(step.title, market), description: localiseTerms(step.description, market) }));
  return (
    <section aria-labelledby="chemical-overhaul-process-title" className="px-2">
      <h2
        id="chemical-overhaul-process-title"
        className="text-xl md:text-2xl text-center font-bold uppercase text-gray-800"
      >
        {title ?? localiseTerms('9-Step Aircon Chemical Overhaul Process', market)}
      </h2>

      <p className="text-center leading-6 font-medium mt-6 text-gray-600">
        {intro ?? (market
          ? <>The <strong>chemical overhaul</strong> is a complete deep-cleaning process — ideal for severely clogged or underperforming {localiseTerms('aircon units', market)}.</>
          : <>The <strong>chemical overhaul</strong> is a complete deep-cleaning process — ideal for severely clogged or underperforming aircon units.</>)}
      </p>

      <ul
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        aria-label={localiseTerms("Aircon chemical overhaul steps", market)}
      >
        {items.map((step, index) => (
          <li
            key={index}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition p-6 flex flex-col items-center text-center"
            aria-labelledby={`overhaul-step-title-${index}`}
          >
            <div>{step.icon}</div>
            <h3
              id={`overhaul-step-title-${index}`}
              className="md:text-lg font-semibold mt-4 text-gray-800"
            >
              {step.title}
            </h3>
            <p className="text-sm leading-6 text-gray-600 mt-2">{step.description}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};
